import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-5">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="mb-10 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00E676]">
            <Zap className="h-5 w-5 text-black" fill="black" />
          </span>
          <span className="font-display text-xl font-black tracking-tight">TipNu</span>
        </Link>

        <h1 className="font-display text-4xl font-black tracking-tight">Welcome back.</h1>
        <p className="mt-2 text-zinc-400">Log in to manage your tip link.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Email</Label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 h-12 border-white/10 bg-[#0F0F0F] text-white focus-visible:ring-[#00E676]"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Password</Label>
            <Input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 h-12 border-white/10 bg-[#0F0F0F] text-white focus-visible:ring-[#00E676]"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-400">
              {error}
            </p>
          )}

          <div className="flex items-center justify-between text-sm">
            <Link to="/forgot-username" data-testid="login-forgot-username" className="text-zinc-400 hover:text-[#00E676]">
              Forgot username?
            </Link>
            <Link to="/forgot-password" data-testid="login-forgot-password" className="text-zinc-400 hover:text-[#00E676]">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="flex h-12 w-full items-center justify-center rounded-full bg-[#00E676] font-bold text-black transition-transform active:scale-95 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log in"}
          </button>
        </form>

        <p className="mt-6 text-sm text-zinc-500">
          No account?{" "}
          <Link to="/register" data-testid="login-to-register" className="font-semibold text-[#00E676]">
            Create your tip link
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
